import { Node } from "reactflow";
import { getDataTypeFromSchema, getSchema } from "../NodeTypes/DataSchemas";
import { IDataParam, ITemplateEditorOptions } from "../NodeTypes/Base";
import {
  DocumentSchemaType,
  IDocumentListSchema,
  IDocumentObjectSchema,
  IDocumentSchema
} from "../FlowDocument/FlowDocumentModel";
import { camelCaseToReadable } from "../Util/NodeUtil";
import { DataType, isListType, isObjectLike } from "../NodeTypes/TypeDefinitions";

/** A single Handlebars token the user can insert into a String Format template. */
export interface ITemplateToken {
  /** Friendly label (schema property label, or a readable form of the raw name). */
  label: string;
  /** Exact text inserted into the template, e.g. "{{firstName}}" or "{{this}}". */
  token: string;
  /** Optional human-readable label for the underlying value's data type. */
  dataTypeLabel?: string;
  /** Optional grouping heading, e.g. "Item properties". */
  group?: string;
}

const eachPathScaffold = (path: string) => `{{#each ${path}}}\n  \n{{/each}}`;
const eachScaffold = eachPathScaffold("this");

/**
 * Maps an object schema's properties to insertable tokens. The token text always uses the RAW
 * property name (the dictionary key) while the label prefers the property's display label — this is
 * the label→raw-name mapping users cannot otherwise see.
 *
 * Nested object properties (including references — a reference schema is just an object schema) are
 * expanded into their own dot-path tokens (e.g. {{address.city}}, {{vendor.displayName}}); nested
 * object-list properties get their own each-scaffold plus flattened item tokens. Primitives stay as a
 * single flat token. `visitedSchemaIds` guards against self/mutually-referential schemas causing
 * infinite recursion.
 */
const objectSchemaToTokens = (
  objectSchema: IDocumentObjectSchema,
  group?: string,
  pathPrefix = "",
  visitedSchemaIds: Set<string> = new Set()
): ITemplateToken[] => {
  if (!objectSchema?.properties || visitedSchemaIds.has(objectSchema.id)) {
    return [];
  }
  const visited = new Set(visitedSchemaIds).add(objectSchema.id);

  return Object.keys(objectSchema.properties).flatMap((propertyName) => {
    const property = objectSchema.properties[propertyName];
    // Matches the node's own handle-label convention (HandleContainer): strip underscores even off an explicit label.
    const label = (property?.label ?? camelCaseToReadable(propertyName)).replaceAll("_", " ");
    const path = `${pathPrefix}${propertyName}`;
    const propertySchema = property?.schema ? (getSchema(property.schema) as unknown as IDocumentSchema) : undefined;

    if (propertySchema) {
      if (propertySchema.schemaType === DocumentSchemaType.object) {
        return objectSchemaToTokens(propertySchema as unknown as IDocumentObjectSchema, label, `${path}.`, visited);
      }

      if (propertySchema.schemaType === DocumentSchemaType.list) {
        const itemSchema = getSchema(
          (propertySchema as unknown as IDocumentListSchema).listItemSchema
        ) as unknown as IDocumentSchema;
        if (itemSchema?.schemaType === DocumentSchemaType.object) {
          return [
            { label: `Repeat for each - ${label}`, token: eachPathScaffold(path), group },
            // Item tokens are relative (no path prefix): they're only valid inside this each-block's scope.
            ...objectSchemaToTokens(itemSchema as unknown as IDocumentObjectSchema, label, "", visited)
          ];
        }
      }
    }

    const dataTypeLabel = property?.schema ? camelCaseToReadable(getDataTypeFromSchema(property.schema)) : undefined;
    return [{ label, token: `{{${path}}}`, dataTypeLabel, group }];
  });
};

/** Resolves the per-item object schema for an object-list input, tolerating either storage shape. */
const resolveItemObjectSchema = (schemaId: string): IDocumentObjectSchema | undefined => {
  const schema = getSchema(schemaId) as unknown as IDocumentSchema;
  if (!schema) {
    return undefined;
  }
  if (schema.schemaType === DocumentSchemaType.list) {
    const itemSchema = getSchema(
      (schema as unknown as IDocumentListSchema).listItemSchema
    ) as unknown as IDocumentSchema;
    return itemSchema?.schemaType === DocumentSchemaType.object
      ? (itemSchema as unknown as IDocumentObjectSchema)
      : undefined;
  }
  return schema.schemaType === DocumentSchemaType.object ? (schema as unknown as IDocumentObjectSchema) : undefined;
};

/**
 * Derives the list of tokens available for a String Format template from the node's current
 * Input Type + Input Schema selection. Coverage matches the backend renderer:
 *  - object       → one {{rawName}} per property
 *  - objectList   → {{#each this}}…{{/each}} scaffold + per-item {{rawName}} tokens
 *  - primitive    → {{this}}
 *  - primitiveList→ {{#each this}}{{this}}{{/each}}
 */
export const getTemplateTokens = (node: Node, options: ITemplateEditorOptions): ITemplateToken[] => {
  // The data param may live on either side (an input feeding the template or an output it's building).
  const dataParam: IDataParam | undefined =
    node.data.nodeProperties.inputs?.[options.dataParam] ?? node.data.nodeProperties.outputs?.[options.dataParam];
  const schemaId = dataParam?.schemaId ?? "";
  // Treat an unresolved data param's type as object.
  const dataType = dataParam?.type ?? DataType.OBJECT;

  if (isObjectLike(dataType)) {
    if (dataType === DataType.OBJECTLIST) {
      const itemSchema = resolveItemObjectSchema(schemaId);
      if (!itemSchema) {
        return [{ label: "Repeat for each item", token: eachScaffold }];
      }
      return [
        { label: "Repeat for each item", token: eachScaffold },
        ...objectSchemaToTokens(itemSchema, "Item properties")
      ];
    }

    const schema = getSchema(schemaId) as unknown as IDocumentObjectSchema;
    if (schema?.schemaType === DocumentSchemaType.object) {
      return objectSchemaToTokens(schema);
    }
    return [];
  }

  if (isListType(dataType)) {
    return [{ label: "Repeat for each item", token: "{{#each this}}{{this}}{{/each}}" }];
  }

  return [{ label: "Value (whole input)", token: "{{this}}" }];
};
